import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { FilterValue } from 'components/breadcrumb/breadcrumbModel';
import { useBreadcrumbStore } from 'hooks/state/useBreadcrumbStore';
import { useAppStore } from 'hooks/state/useAppStore';
import { withSuppressedUrlPersistence } from 'hooks/state/UrlPersistence';
import { ExtendedAttributeCode } from 'pages/trading/model';
import { PageContainer, PageSectionTitle, Tile } from 'components/Layout/PageContainer';
import { Box } from '@mui/material';
import { useDrilldownData, useMessage, useTimeSeries, useTotals } from 'pages/trading/queryHooks';
import { CountryCodeType } from 'models/countries';

import { TradingMessageDisplayTile } from 'pages/trading/TradingMessageDisplay';
import { MeasureCardRow } from 'components/MeasureCardRow';
import { AttributeCode, Measure, useModelService } from 'services/model/model.service';
import { DateRange } from 'components/filter/input/DateRangePicker/types';
import { subDays, subMonths, subWeeks } from 'date-fns';
import { LineChartRenderer } from 'components/chart/renderer/LineChartRenderer';
import { createTimeSeriesChartConfig } from 'utils/createTimeSeriesChartConfig';
import { timeZoneSafeDateString } from 'utils/dateUtils';
import { tradingFiltersToDataRequest } from 'pages/trading/util';
import { TimeSeriesChartPageSection } from 'components/chart/TimeSeriesChartPageSection';
import { AdditionalDateRange } from 'hooks/useDataRequest';
import { ProductRow, ProductsTable } from 'pages/trading/outlier/ProductsTable';
import { DataResult } from 'models/data';
import { CategoriesTable, CategoryRow } from 'pages/trading/outlier/CategoriesTable';
import { extractDrillDownTableDateGmvValues } from 'pages/trading/outlier/DrillDownTableDateRangeRow';
import { ArticleRow, ArticlesTable } from 'pages/trading/outlier/ArticlesTable';
import { MarketplacesRow, MarketplacesTable } from 'pages/trading/outlier/MarketplacesTable';
import { TRADING_ROOT } from 'pages/trading/Trading';
import { MetricsLoadingSpinner } from 'components/MetricsLoadingSpinner';
import { BrandRow, BrandsTable } from 'pages/trading/outlier/BrandsTable';
import { CountriesTable, CountryRow } from 'pages/trading/outlier/CountriesTable';
import { PageRefTile } from 'components/Layout/Tiles/PageRefTile';
import { useInfoDrawer } from 'context/InfoDrawerContext';
import { DrilldownPagesInfoContent } from './DrilldownPagesInfoContent';
import { InfoButton } from 'components/InfoButton';
import { getErrorMessage } from 'utils/getErrorMessage';
import { DrillDownTitle } from 'components/DrillDownTitle';
import { TenantConfigContext } from 'context/TenantConfigContext';
import { productLink } from 'pages/entities/entityhelper';

export function DrillDownPage() {
  const { attributeType, attributeValue } = useParams<{ attributeType: string; attributeValue: string }>();
  const { setDetailPageBreadcrumb, setBreadcrumbs } = useBreadcrumbStore();
  const [filters, setFilters] = useState<Record<ExtendedAttributeCode, FilterValue[]>>();
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { setContent } = useInfoDrawer();
  const { isAdminView } = useContext(TenantConfigContext);

  const customComparisonPeriodText = useMemo(
    () => intl.formatMessage({ id: 'trading.drilldown.comparison.period.previous.weekday' }),
    [intl]
  );

  const { getAttribute, getMeasuresByMetrics } = useModelService();

  const totalMeasures = useMemo(
    () => getMeasuresByMetrics(['ordGmvVal', 'ordQty', 'avgOrdGmvVal', 'ordNum'], 'OD'),
    [getMeasuresByMetrics]
  );
  const [selectedMeasure, setSelectedMeasure] = useState(totalMeasures[0]);

  useEffect(() => {
    const currentSelectedCode = selectedMeasure?.code;
    const newSelectedMeasure = totalMeasures.find(m => m.code === currentSelectedCode) ?? totalMeasures[0];
    if (newSelectedMeasure && newSelectedMeasure !== selectedMeasure) {
      setSelectedMeasure(newSelectedMeasure);
    }
  }, [totalMeasures, selectedMeasure]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [attributeType, attributeValue]);

  useEffect(() => {
    setContent({
      titleKey: 'trading.info.title',
      content: <DrilldownPagesInfoContent />,
    });

    return () => {
      setContent(null);
    };
  }, [setContent]);

  const selectedMeasureChangeHandler = (measure: Measure) => () => {
    if (measure.code !== selectedMeasure.code) {
      setSelectedMeasure(measure);
    }
  };

  const formatDetectionDate = (dateString: string | undefined): string | undefined => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    return intl.formatDate(date, { dateStyle: 'short' });
  };

  useEffect(() => {
    if (attributeValue) {
      const newFilters = setDetailPageBreadcrumb(location.pathname, [
        { attribute: attributeType as AttributeCode, values: [attributeValue] },
      ]);
      setFilters(newFilters);
    }
  }, [attributeValue]);

  const messageId = filters?.messageId && filters?.messageId.length > 0 ? (filters?.messageId[0] as string) : undefined;

  if (filters && !messageId) {
    // If there is no messageId in the filter we didn't come here naturally. Return to messages path
    navigate(`${TRADING_ROOT}${location.search}`);
  }

  const {
    data: message,
    isPending: messageIsPending,
    isSuccess: messageIsSuccess,
    isError: messageIsError,
    error: messageError,
  } = useMessage(messageId);

  const getComparisonDateRange = () => {
    if (filters) {
      const dateRange = filters.dateRange && filters.dateRange.length > 0 ? (filters.dateRange[0] as DateRange) : null;
      if (dateRange) {
        return {
          from: dateRange.from ? subWeeks(dateRange.from, 1) : undefined,
          to: dateRange.to ? subWeeks(dateRange.to, 1) : undefined,
        };
      }
    }
    return undefined;
  };
  const mainComparisonDateRange = getComparisonDateRange();

  const {
    data: totals,
    isError: totalsIsError,
    error: totalsError,
  } = useTotals('country', filters, mainComparisonDateRange);

  const parentTotal: number = selectedMeasure.getValue(totals?.values);

  const createTimeSeriesRequest = () => {
    if (filters) {
      const dateRange = filters.dateRange && filters.dateRange.length > 0 ? (filters.dateRange[0] as DateRange) : null;

      if (dateRange?.from) {
        const timeSeriesDateRange = {
          from: subDays(dateRange.from, 30),
          to: dateRange.to,
        };

        const comparisonDateRange = {
          from: timeSeriesDateRange.from ? subWeeks(timeSeriesDateRange.from, 1) : undefined,
          to: timeSeriesDateRange.to ? subWeeks(timeSeriesDateRange.to, 1) : undefined,
        };

        const amendedFilters = { ...filters, dateRange: [timeSeriesDateRange] };

        return tradingFiltersToDataRequest(amendedFilters, isAdminView, comparisonDateRange);
      }
    }
    return undefined;
  };

  const createDataTableRequest = () => {
    if (filters) {
      const dateRange = filters.dateRange && filters.dateRange.length > 0 ? (filters.dateRange[0] as DateRange) : null;

      if (dateRange?.from && dateRange.to) {
        const timeSeriesDateRange = {
          from: subMonths(dateRange.from, 1),
          to: dateRange.to,
        };

        const comparisonDateRange = {
          from: subWeeks(dateRange.from, 2),
          to: subWeeks(dateRange.to, 1),
        };

        const onDayRange = {
          key: 'onDayRange',
          timeFilter: {
            fromDate: timeZoneSafeDateString(dateRange.from),
            toDate: timeZoneSafeDateString(dateRange.to),
          },
          comparisonTimeFilter: {
            fromDate: dateRange.from ? timeZoneSafeDateString(subWeeks(dateRange.from, 1)) : undefined,
            toDate: dateRange.from ? timeZoneSafeDateString(subWeeks(dateRange.from, 1)) : undefined,
          },
        } as AdditionalDateRange;

        // We pass the one day time frame into the additional slot as we want the query inclusions based on the weekly range
        const onWeekRange = {
          key: 'onWeekRange',
          timeFilter: {
            fromDate: timeZoneSafeDateString(subDays(dateRange.from, 6)),
            toDate: timeZoneSafeDateString(dateRange.to),
          },
          comparisonTimeFilter: {
            fromDate: dateRange.from ? timeZoneSafeDateString(subDays(dateRange.from, 13)) : undefined,
            toDate: dateRange.from ? timeZoneSafeDateString(subWeeks(dateRange.from, 1)) : undefined,
          },
        } as AdditionalDateRange;

        const onWeek2 = {
          key: 'onWeek2',
          timeFilter: {
            fromDate: timeZoneSafeDateString(subWeeks(dateRange.from, 2)),
            toDate: timeZoneSafeDateString(subWeeks(dateRange.from, 2)),
          },
        } as AdditionalDateRange;

        const onWeek3 = {
          key: 'onWeek3',
          timeFilter: {
            fromDate: timeZoneSafeDateString(subWeeks(dateRange.from, 3)),
            toDate: timeZoneSafeDateString(subWeeks(dateRange.from, 3)),
          },
        } as AdditionalDateRange;

        const onWeek4 = {
          key: 'onWeek4',
          timeFilter: {
            fromDate: timeZoneSafeDateString(subWeeks(dateRange.from, 4)),
            toDate: timeZoneSafeDateString(subWeeks(dateRange.from, 4)),
          },
        } as AdditionalDateRange;

        const amendedFilters = { ...filters, dateRange: [timeSeriesDateRange] };

        return tradingFiltersToDataRequest(amendedFilters, isAdminView, comparisonDateRange, [
          onDayRange,
          onWeekRange,
          onWeek2,
          onWeek3,
          onWeek4,
        ]);
      }
    }
    return undefined;
  };

  const request = createTimeSeriesRequest();

  const {
    data: timeseriesData,
    isError: timeSeriesIsError,
    error: timeSeriesError,
  } = useTimeSeries(selectedMeasure.code, request);

  const timeseriesRenderer = useMemo(() => {
    if (timeseriesData && request) {
      const dateRange = request.timeFilter!;
      const mainLabel =
        dateRange.fromDate && dateRange?.toDate
          ? intl.formatDateTimeRange(new Date(dateRange.fromDate), new Date(dateRange.toDate))
          : '';
      return new LineChartRenderer(
        createTimeSeriesChartConfig({
          getAttribute,
          measures: [selectedMeasure],
          request,
          intl,
          response: timeseriesData,
          customDateRangeLabel: mainLabel,
          customComparisonRangeLabel:
            customComparisonPeriodText[0].toLocaleUpperCase() + customComparisonPeriodText.slice(1),
          showDetectionDateMarker: true,
        })
      );
    }
    return undefined;
  }, [timeseriesData, intl, filters, formatDetectionDate, selectedMeasure]);

  const showMarketplaces = !!filters && !filters.chnMarketplace;
  const showProducts = !!filters && !!filters.prdCategory && !filters.prdNumber;
  const showCategories = !!filters && !filters.prdCategory;
  const showArticles = !!filters && !!filters.prdNumber;
  const showBrands = !!filters && !filters.prdBrand;
  const showCountries = !!filters && !filters.chnCountry;

  const {
    data: marketplaceDrillDownData,
    isError: isMarketPlaceError,
    error: marketPlaceError,
  } = useDrilldownData('marketplaces', selectedMeasure.code, showMarketplaces, createDataTableRequest());

  const {
    data: productDrillDownData,
    isError: isProductError,
    error: productError,
  } = useDrilldownData('products', selectedMeasure.code, showProducts, createDataTableRequest());

  const {
    data: categoryDrillDownData,
    isError: isCategoryError,
    error: categoryError,
  } = useDrilldownData('categories', selectedMeasure.code, showCategories, createDataTableRequest());

  const {
    data: articleDrillDownData,
    isError: isArticleError,
    error: articleError,
  } = useDrilldownData('articles', selectedMeasure.code, showArticles, createDataTableRequest());

  const {
    data: brandDrillDownData,
    isError: isBrandError,
    error: brandError,
  } = useDrilldownData('brands', selectedMeasure.code, showBrands, createDataTableRequest());

  const {
    data: countryDrillDownData,
    isError: isCountryError,
    error: countryError,
  } = useDrilldownData('countries', selectedMeasure.code, showCountries, createDataTableRequest());

  const flattenArticlesData = (data: DataResult): ArticleRow[] =>
    data.sets.table.map(row => ({
      ...extractDrillDownTableDateGmvValues(row, parentTotal, selectedMeasure),
      artImageUrl: row.attributes.artImageUrl,
      artBrandColor: row.attributes.artBrandColor,
      artSize: row.attributes.artSize,
      artEan: row.attributes.artEan,
      prdName: row.attributes.prdName,
    }));

  const flattenProductsData = (data: DataResult): ProductRow[] =>
    data.sets.table.map(row => ({
      ...extractDrillDownTableDateGmvValues(row, parentTotal, selectedMeasure),
      artImageUrl: row.attributes.artImageUrl,
      prdName: row.attributes.prdName,
      prdNumber: row.attributes.prdNumber,
    }));

  const flattenCategoriesData = (data: DataResult): CategoryRow[] =>
    data.sets.table.map(row => ({
      ...extractDrillDownTableDateGmvValues(row, parentTotal, selectedMeasure),
      prdCategory: row.attributes.prdCategory,
    }));

  const flattenBrandsData = (data: DataResult): BrandRow[] =>
    data.sets.table.map(row => ({
      ...extractDrillDownTableDateGmvValues(row, parentTotal, selectedMeasure),
      prdBrand: row.attributes.prdBrand,
    }));

  const flattenCountriesData = (data: DataResult): CountryRow[] =>
    data.sets.table.map(row => ({
      ...extractDrillDownTableDateGmvValues(row, parentTotal, selectedMeasure),
      chnCountry: row.attributes.chnCountry,
    }));

  const flattenMarketplacesData = (data: DataResult): MarketplacesRow[] =>
    data.sets.table.map(row => ({
      ...extractDrillDownTableDateGmvValues(row, parentTotal, selectedMeasure),
      chnMarketplace: row.attributes.chnMarketplace,
    }));

  const detectionDate = useMemo(() => {
    if (filters?.dateRange && filters.dateRange.length > 0) {
      return formatDetectionDate(timeZoneSafeDateString((filters.dateRange[0] as DateRange).to));
    }
    return formatDetectionDate(message?.currentDataVersion);
  }, [filters, message?.currentDataVersion]);

  const productInfo = useMemo(() => {
    if (filters?.prdNumber && filters.prdNumber.length > 0) {
      const productNumber = filters.prdNumber[0] as string;
      const productName =
        articleDrillDownData?.sets.table[0]?.attributes.prdName ||
        productDrillDownData?.sets.table.find(row => row.attributes.prdNumber === productNumber)?.attributes.prdName ||
        productNumber;
      return { productNumber, productName };
    }
    return null;
  }, [filters, articleDrillDownData, productDrillDownData]);

  const { setDateRange, setProducts, setCategories, setBrands, setMarketplaces, setCountries, clearAll } =
    useAppStore();

  const getDateRange = () =>
    filters?.dateRange && filters.dateRange.length > 0 ? (filters.dateRange[0] as DateRange) : null;

  const applyDateRangeToStore = (dateRange: DateRange | null) => {
    if (dateRange) {
      const rangeFrom = dateRange.from;
      const comparisonFrom = rangeFrom ? subWeeks(rangeFrom, 1) : undefined;
      const comparisonTo = dateRange.to ? subWeeks(dateRange.to, 1) : undefined;
      setDateRange({
        range: {
          from: rangeFrom,
          to: dateRange.to,
          presetValue: 'custom',
        },
        comparison: {
          from: comparisonFrom,
          to: comparisonTo,
          presetValue: 'custom',
        },
      });
    }
  };

  const applyFiltersToStore = (filtersToApply?: Record<ExtendedAttributeCode, FilterValue[]>) => {
    const filterSource = filtersToApply || filters;
    if (!filterSource) return;
    if (filterSource.prdCategory) setCategories(filterSource.prdCategory as string[]);
    if (filterSource.prdBrand) setBrands(filterSource.prdBrand as string[]);
    if (filterSource.chnMarketplace) setMarketplaces(filterSource.chnMarketplace as string[]);
    if (filterSource.chnCountry) setCountries(filterSource.chnCountry as CountryCodeType[]);
  };

  const getAccountNumberParam = () => {
    const accountNumber = new URLSearchParams(location.search).get('accountNumber');
    return accountNumber ? `?accountNumber=${accountNumber}` : '';
  };

  const handleDetailsClick = () => {
    if (filters && filters.prdNumber && filters.prdNumber.length > 0) {
      const productNumber = filters.prdNumber[0] as string;
      const dateRange = getDateRange();
      const currentFilters = filters;

      navigate(productLink(productNumber, 'performance'));
      withSuppressedUrlPersistence(() => {
        clearAll();
        applyDateRangeToStore(dateRange ?? null);
        applyFiltersToStore(currentFilters);
        setProducts([productNumber]);
        setBreadcrumbs([]);
      });
    }
  };

  const handleTopSellersClick = () => {
    if (filters) {
      const dateRange = getDateRange();
      const currentFilters = filters;

      navigate(`/insights/product/reports/top-selling${getAccountNumberParam()}`);
      withSuppressedUrlPersistence(() => {
        clearAll();
        applyDateRangeToStore(dateRange ?? null);
        applyFiltersToStore(currentFilters);
        setBreadcrumbs([]);
      });
    }
  };

  const handleArticleVisibilityClick = () => {
    if (filters && filters.prdNumber && filters.prdNumber.length > 0) {
      const productNumber = filters.prdNumber[0] as string;
      const dateRange = getDateRange();
      const currentFilters = filters;

      navigate(`/articlevisibility${getAccountNumberParam()}`);
      withSuppressedUrlPersistence(() => {
        clearAll();
        applyDateRangeToStore(dateRange ?? null);
        applyFiltersToStore(currentFilters);
        setProducts([productNumber]);
        setBreadcrumbs([]);
      });
    }
  };

  return (
    <div className="standard-page-container">
      <PageContainer className="mb-0">
        <PageSectionTitle
          rightComponent={<InfoButton tooltipText="View page information" showText />}
          testId="trading-drilldown-title"
        >
          <DrillDownTitle filterAttributes={filters ?? ({} as Record<ExtendedAttributeCode, FilterValue[]>)} />
        </PageSectionTitle>

        {messageIsPending && (
          <Tile className="p-0">
            <Box className="flex justify-center py-8">
              <MetricsLoadingSpinner />
            </Box>
          </Tile>
        )}
        {messageIsError && <Tile errorDetails={getErrorMessage(messageError)} />}
        {messageIsSuccess && <TradingMessageDisplayTile message={message} testId="trading-drilldown-message" />}

        {totalsIsError || timeSeriesIsError ? (
          <Tile
            errorDetails={getErrorMessage(totalsError) ?? getErrorMessage(timeSeriesError)}
            testId="trading-drilldown-overview-error"
          />
        ) : (
          <>
            {totals && (
              <MeasureCardRow
                measures={totalMeasures}
                testid="pro-performance-measure-card-set"
                totalsData={totals}
                customComparisonPeriod={customComparisonPeriodText}
                selectedMeasureCode={selectedMeasure.code}
                onMeasureSelect={selectedMeasureChangeHandler}
                detectionDate={detectionDate}
              />
            )}

            {timeseriesRenderer && (
              <TimeSeriesChartPageSection
                timeSeriesMeasure={selectedMeasure}
                timeSeriesRenderer={timeseriesRenderer}
                dataTestId="trading-drilldown-timeline"
                showGroupByToggle={false}
              />
            )}

            {showMarketplaces && isMarketPlaceError && (
              <Tile
                titleId="insights.marketplaces"
                errorDetails={getErrorMessage(marketPlaceError)}
                testId="trading-drilldown-marketplaces-error"
              />
            )}
            {showMarketplaces && marketplaceDrillDownData && (
              <MarketplacesTable
                rows={flattenMarketplacesData(marketplaceDrillDownData)}
                measure={selectedMeasure}
                sentiment={message?.sentiment}
                detectionDate={detectionDate}
              />
            )}
            {showCountries && isCountryError && (
              <Tile
                titleId="trading.analysis.level.header.COUNTRY"
                errorDetails={getErrorMessage(countryError)}
                testId="trading-drilldown-countries-error"
              />
            )}
            {showCountries && countryDrillDownData && (
              <CountriesTable
                rows={flattenCountriesData(countryDrillDownData)}
                measure={selectedMeasure}
                sentiment={message?.sentiment}
                detectionDate={detectionDate}
              />
            )}
            {showBrands && isBrandError && (
              <Tile
                titleId="trading.analysis.level.header.BRAND"
                errorDetails={getErrorMessage(brandError)}
                testId="trading-drilldown-brands-error"
              />
            )}
            {showBrands && brandDrillDownData && (
              <BrandsTable
                rows={flattenBrandsData(brandDrillDownData)}
                measure={selectedMeasure}
                sentiment={message?.sentiment}
                detectionDate={detectionDate}
              />
            )}
            {showCategories && isCategoryError && (
              <Tile
                titleId="insights.category"
                errorDetails={getErrorMessage(categoryError)}
                testId="trading-drilldown-categories-error"
              />
            )}
            {showCategories && categoryDrillDownData && (
              <CategoriesTable
                rows={flattenCategoriesData(categoryDrillDownData)}
                measure={selectedMeasure}
                sentiment={message?.sentiment}
                detectionDate={detectionDate}
              />
            )}
            {showProducts && isProductError && (
              <Tile
                titleId="insights.products"
                errorDetails={getErrorMessage(productError)}
                testId="trading-drilldown-products-error"
              />
            )}
            {showProducts && productDrillDownData && (
              <ProductsTable
                rows={flattenProductsData(productDrillDownData)}
                measure={selectedMeasure}
                sentiment={message?.sentiment}
                detectionDate={detectionDate}
              />
            )}
            {showArticles && isArticleError && (
              <Tile
                titleId="insights.articles"
                errorDetails={getErrorMessage(articleError)}
                testId="trading-drilldown-articles-error"
              />
            )}
            {showArticles && articleDrillDownData && (
              <ArticlesTable
                rows={flattenArticlesData(articleDrillDownData)}
                measure={selectedMeasure}
                sentiment={message?.sentiment}
                detectionDate={detectionDate}
              />
            )}
          </>
        )}
        {productInfo && (
          <>
            <PageSectionTitle
              titleId="general.title.explore_more"
              additionalTitle={<span className="text-2xl ml-[-10px]">...</span>}
            />
            <PageRefTile
              testId="tile-details"
              className="xl:col-span-4"
              iconName="Analytics"
              title={{ id: 'trading.drilldown.tile.details.title' }}
              description={{
                id: 'trading.drilldown.tile.details.description',
                values: { productName: productInfo.productName },
              }}
              buttonText={{ id: 'trading.drilldown.tile.details.button' }}
              onClick={handleDetailsClick}
            />
            <PageRefTile
              testId="tile-topsellers"
              className="xl:col-span-4"
              iconName="Analytics"
              title={{ id: 'trading.drilldown.tile.topsellers.title' }}
              description={{
                id: 'trading.drilldown.tile.topsellers.description',
                values: {
                  messageDate: detectionDate,
                  timeframe: '30 days',
                  category: filters?.prdCategory?.length
                    ? getAttribute('prdCategory').translate(filters.prdCategory[0] as string)
                    : undefined,
                },
              }}
              buttonText={{ id: 'trading.drilldown.tile.topsellers.button' }}
              onClick={handleTopSellersClick}
            />
            <PageRefTile
              testId="tile-article-visibility"
              className="xl:col-span-4"
              iconName="ArticleVisibilityFilled"
              title={{ id: 'articlevisibility' }}
              description={{
                id: 'trading.drilldown.tile.articlevisibility.description',
                values: { productName: productInfo.productName },
              }}
              buttonText={{ id: 'trading.drilldown.tile.articlevisibility.button' }}
              onClick={handleArticleVisibilityClick}
            />
          </>
        )}
      </PageContainer>
    </div>
  );
}
